import { getSupabaseClient } from '../utils/supabase';
import { VehicleType } from '../types';
import logger from '../utils/logger';

/**
 * 車種マスタ管理のサービス関数
 */
export class VehicleTypeService {
  /**
   * 全車種を取得
   */
  static async getAll(): Promise<VehicleType[]> {
    try {
      const supabase = getSupabaseClient();
      if (!supabase) {
        throw new Error('Supabaseクライアントが初期化されていません');
      }

      const { data, error } = await supabase.from('vehicle_types').select('*').order('name');

      if (error) {
        logger.error('車種取得エラー:', error);
        throw new Error(`車種の取得に失敗しました: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      logger.error('車種取得エラー:', error);
      throw error;
    }
  }

  /**
   * 特定の車種を取得
   */
  static async getById(id: string): Promise<VehicleType | null> {
    try {
      const supabase = getSupabaseClient();
      if (!supabase) {
        throw new Error('Supabaseクライアントが初期化されていません');
      }

      const { data, error } = await supabase
        .from('vehicle_types')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // レコードが見つからない
        }
        logger.error('車種取得エラー:', error);
        throw new Error(`車種の取得に失敗しました: ${error.message}`);
      }

      return data;
    } catch (error) {
      logger.error('車種取得エラー:', error);
      throw error;
    }
  }

  /**
   * 車種を新規作成
   */
  static async create(vehicleType: Omit<VehicleType, 'id'>): Promise<VehicleType> {
    try {
      // 同じ名前の車種が既に存在するかチェック
      const existingVehicleType = await this.getByName(vehicleType.name);
      if (existingVehicleType) {
        throw new Error(`車種「${vehicleType.name}」は既に存在します`);
      }

      const supabase = getSupabaseClient();
      if (!supabase) {
        throw new Error('Supabaseクライアントが初期化されていません');
      }

      const newVehicleType: VehicleType = {
        id: crypto.randomUUID(),
        ...vehicleType,
      };

      const { data, error } = await supabase
        .from('vehicle_types')
        .insert([newVehicleType])
        .select()
        .single();

      if (error) {
        logger.error('車種作成エラー:', error);
        throw new Error(`車種の作成に失敗しました: ${error.message}`);
      }

      return data;
    } catch (error) {
      logger.error('車種作成エラー:', error);
      throw error;
    }
  }

  /**
   * 車種を更新
   */
  static async update(id: string, updates: Partial<Omit<VehicleType, 'id'>>): Promise<VehicleType> {
    try {
      // 名前の変更がある場合、重複チェック
      if (updates.name) {
        const existingVehicleType = await this.getByName(updates.name);
        if (existingVehicleType && existingVehicleType.id !== id) {
          throw new Error(`車種「${updates.name}」は既に存在します`);
        }
      }

      const supabase = getSupabaseClient();
      if (!supabase) {
        throw new Error('Supabaseクライアントが初期化されていません');
      }

      const { data, error } = await supabase
        .from('vehicle_types')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        logger.error('車種更新エラー:', error);
        throw new Error(`車種の更新に失敗しました: ${error.message}`);
      }

      if (!data) {
        throw new Error('更新対象の車種が見つかりません');
      }

      return data;
    } catch (error) {
      logger.error('車種更新エラー:', error);
      throw error;
    }
  }

  /**
   * 車種を削除
   */
  static async delete(id: string): Promise<void> {
    try {
      // 削除前に関連データの確認
      await this.checkDependencies(id);

      const supabase = getSupabaseClient();
      if (!supabase) {
        throw new Error('Supabaseクライアントが初期化されていません');
      }

      const { error } = await supabase.from('vehicle_types').delete().eq('id', id);

      if (error) {
        logger.error('車種削除エラー:', error);
        throw new Error(`車種の削除に失敗しました: ${error.message}`);
      }
    } catch (error) {
      logger.error('車種削除エラー:', error);
      throw error;
    }
  }

  /**
   * 名前で車種を検索（重複チェック用）
   */
  static async getByName(name: string): Promise<VehicleType | null> {
    try {
      const supabase = getSupabaseClient();
      if (!supabase) {
        throw new Error('Supabaseクライアントが初期化されていません');
      }

      const { data, error } = await supabase
        .from('vehicle_types')
        .select('*')
        .eq('name', name.trim())
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // レコードが見つからない
        }
        logger.error('車種検索エラー:', error);
        throw new Error(`車種の検索に失敗しました: ${error.message}`);
      }

      return data;
    } catch (error) {
      logger.error('車種検索エラー:', error);
      throw error;
    }
  }

  /**
   * 車種の依存関係をチェック（削除前確認用）
   */
  static async checkDependencies(id: string): Promise<void> {
    const supabase = getSupabaseClient();
    if (!supabase) {
      throw new Error('Supabaseクライアントが初期化されていません');
    }

    // 車両モデルとの関連をチェック
    const { data: vehicleModels, error: vehicleModelsError } = await supabase
      .from('vehicle_models')
      .select('id, name')
      .eq('vehicle_type_id', id)
      .limit(1);

    if (vehicleModelsError) {
      logger.error('依存関係チェックエラー:', vehicleModelsError);
      throw new Error('依存関係の確認に失敗しました');
    }

    if (vehicleModels && vehicleModels.length > 0) {
      throw new Error(
        'この車種は車両モデルで使用されているため削除できません。\n' +
          '先に関連する車両モデルを削除または変更してください。'
      );
    }

    // 将来的に他のテーブルとの関連もここでチェック
    // 例: 案件での使用、会社車両での使用など
  }

  /**
   * 車種の利用統計を取得
   */
  static async getUsageStats(id: string): Promise<{
    vehicleModelsCount: number;
    vehiclesCount: number;
    casesCount: number;
  }> {
    try {
      const supabase = getSupabaseClient();
      if (!supabase) {
        throw new Error('Supabaseクライアントが初期化されていません');
      }

      // 車両モデル数の取得
      const { count: vehicleModelsCount, error: vehicleModelsError } = await supabase
        .from('vehicle_models')
        .select('*', { count: 'exact', head: true })
        .eq('vehicle_type_id', id);

      if (vehicleModelsError) {
        logger.error('車両モデル数取得エラー:', vehicleModelsError);
        throw new Error('統計情報の取得に失敗しました');
      }

      // 車両数の取得（vehicle_modelsを経由）
      const { data: vehicleModels } = await supabase
        .from('vehicle_models')
        .select('id')
        .eq('vehicle_type_id', id);

      let vehiclesCount = 0;
      if (vehicleModels && vehicleModels.length > 0) {
        const modelIds = vehicleModels.map(vm => vm.id);
        const { count, error: vehiclesError } = await supabase
          .from('vehicles')
          .select('*', { count: 'exact', head: true })
          .in('model_id', modelIds);

        if (vehiclesError) {
          logger.error('車両数取得エラー:', vehiclesError);
          throw new Error('統計情報の取得に失敗しました');
        }

        vehiclesCount = count || 0;
      }

      // 案件数の取得（将来的に実装）
      const casesCount = 0; // TODO: 案件テーブルとの関連実装後に対応

      return {
        vehicleModelsCount: vehicleModelsCount || 0,
        vehiclesCount,
        casesCount,
      };
    } catch (error) {
      logger.error('利用統計取得エラー:', error);
      throw error;
    }
  }
}
